/*
  Lunya brand adapter: the source's PDP description clamp (lunya/snippets/product-hero-description.liquid,
  jQuery shorten at 125 characters with READ MORE / READ LESS). The full description stays in the DOM
  and is toggled by a real button with aria-expanded.

  Inlined immediately after the description so this runs in the parser's task, before paint.
  The branded layout also defers this asset before main.js so the first Quick Buy open is ready.
  Initialization is idempotent across inline/deferred execution, section reloads and repeated
  Quick Buy opens.

  Symmetry replaces the description block on every variant change (its data-dynamic-variant-content
  boundary) and on theme-editor section reloads. Incoming fragments are enhanced before insertion
  so they cannot paint expanded while waiting for the delayed variant event. A shopper's READ MORE
  survives that product's variant replacements, and so does keyboard focus: replacement removes the
  focused node, which would otherwise drop focus to <body>.
*/
(() => {
  window.theme = window.theme || {};
  if (theme.lunyaDescriptionApply) {
    theme.lunyaDescriptionApply(document);
    return;
  }

  const LIMIT = 125;
  // Wrapper identity keeps separate products and PDP/Quick Buy instances isolated.
  const expanded = new WeakSet();
  let nextId = 0;
  let refocus = null;

  document.addEventListener('on:variant:before-replace-element', (event) => {
    const id = event.detail?.toReplace?.dataset?.lunyaDescription;
    const toggle = id && event.detail.toReplace.querySelector('.lunya-description-toggle');
    if (toggle && toggle === document.activeElement) refocus = event.detail.replaceWith;
    if (id && event.detail.replaceWith) {
      const { toReplace, replaceWith } = event.detail;
      if (expanded.has(toReplace)
          && id === replaceWith.dataset.lunyaDescription
          && toReplace.dataset.lunyaDescriptionProduct === replaceWith.dataset.lunyaDescriptionProduct) {
        expanded.add(replaceWith);
      }
      apply(replaceWith);
    }
  });

  const apply = (root = document) => {
    const selector = '[data-lunya-description]:not([data-lunya-description-ready])';
    const wrappers = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) wrappers.unshift(root);
    wrappers.forEach((wrapper) => {
      const body = wrapper.querySelector('.product-description');
      const blockId = wrapper.dataset.lunyaDescription;
      wrapper.setAttribute('data-lunya-description-ready', '');
      if (!body) return;

      const copy = body.cloneNode(true);
      copy.querySelectorAll('br, p, div, li, dt, dd, tr, td, th, h1, h2, h3, h4, h5, h6, blockquote, pre').forEach(node => node.after(' '));
      const text = copy.textContent.replace(/\s+/g, ' ').trim();
      const characters = Array.from(text);
      if (characters.length <= LIMIT) return;

      // PDP and Quick Buy may share a template block ID. Their controls must
      // reference different bodies, including fragments prepared while detached.
      const id = `LunyaDescription-${blockId}-${++nextId}`;
      const short = document.createElement('p');
      short.className = 'lunya-description__short';
      short.textContent = `${characters.slice(0, LIMIT).join('').trimEnd()}...`;

      body.id = id;
      body.hidden = true;

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'lunya-description-toggle';
      toggle.setAttribute('aria-controls', id);
      toggle.setAttribute('aria-expanded', 'false');
      const show = (open) => {
        toggle.setAttribute('aria-expanded', String(open));
        body.hidden = !open;
        short.hidden = open;
        toggle.textContent = open ? wrapper.dataset.lunyaReadLess : wrapper.dataset.lunyaReadMore;
        if (open) expanded.add(wrapper);
        else expanded.delete(wrapper);
      };
      toggle.addEventListener('click', () => show(toggle.getAttribute('aria-expanded') !== 'true'));

      body.before(short);
      body.after(toggle);
      show(expanded.has(wrapper));
    });
  };

  theme.lunyaDescriptionApply = apply;
  apply();
  document.addEventListener('on:variant:change', () => {
    apply();
    if (refocus) {
      // Incoming nodes are enhanced while detached; restore focus after insertion.
      if (refocus.isConnected) refocus.querySelector('.lunya-description-toggle')?.focus({ preventScroll: true });
      refocus = null;
    }
  });
  document.addEventListener('on:quickbuy:after-open', (event) => apply(event.target));
  document.addEventListener('shopify:section:load', (event) => apply(event.target));
})();

